import { inject, injectable, optional } from "inversify";
import { DomElementsService, Logger, StatsPanel } from "@designboticteam/core";
import { type IRendererService, RENDERER_SERVICE } from "../RendererService/IRendererService";

/** Time window for collecting a single FPS sample */
const SAMPLE_INTERVAL_MS = 250;

/** Number of FPS samples to collect before evaluating quality */
const SAMPLES_PER_EVALUATION = 10;

/** Fraction of samples that must exceed bounds to trigger a quality change (0-1) */
const DECISION_THRESHOLD = 0.75;

/** How much the quality factor changes per evaluation step (0-1) */
const QUALITY_STEP = 0.1;

/** Max quality oscillations (up/down) before triggering fallback */
const MAX_FLIP_FLOPS = Infinity;

// DPR range: qualityFactor 0 -> MIN_DPR, qualityFactor 1 -> MAX_DPR
const MIN_DPR = 0.5;
const MAX_DPR = window.devicePixelRatio;

/**
 * Returns [min, max] acceptable FPS range based on the highest observed FPS (≈ display refresh rate).
 * E.g.
 * - on a 120Hz display (displayRefreshRate=120) -> [60, 100]
 * - on a 60Hz display (displayRefreshRate=60) -> [40, 60].
 * FPS above max -> onIncline (quality up), FPS below min -> onDecline (quality down).
 */
function getAcceptableFpsRange(displayRefreshRate: number): [number, number] {
	return displayRefreshRate > 100 ? [60, 100] : [40, 60];
}

/**
 * @name PerformanceMonitorService
 * @description Monitors rendering performance and adaptively adjusts quality settings (DPR, shadows) to maintain smooth FPS.
 * Highly inspired by: https://raw.githubusercontent.com/pmndrs/drei/refs/heads/master/src/core/PerformanceMonitor.tsx
 * - removed React dependency
 * - fix flip-flopp logic (infinite increment bug)
 */
@injectable()
class PerformanceMonitorService {
	public readonly serviceName = "📊 PerformanceMonitorService";
	private readonly logger = new Logger(`📊 WORKSPACE 3D | ${this.serviceName}`, {
		color: "#ffffff",
		backgroundColor: "#8b5cf6",
	});

	/** Whether the adaptive quality system is still running (stops after too many flip-flops) */
	private isAdaptivePerformanceActive = false;
	/** Last measured FPS value */
	private currentFps = 0;
	/** Quality level between 0 (lowest) and 1 (highest), adjusted by adaptive logic */
	private qualityFactor = 0.5;
	/** Highest FPS ever observed, used to estimate the display's refresh rate */
	private displayRefreshRate = 0;
	/** Timestamps of frames within the current sample window */
	private frameTimes: number[] = [];
	/** Collected FPS averages, one per sample window, used to evaluate quality */
	private fpsSamples: number[] = [];
	/** Rolling index into fpsSamples array */
	private sampleIndex = 0;
	/** How many times quality has oscillated between incline and decline */
	private flipFlopCount = 0;

	private statsPanel: StatsPanel | null = null;
	/** Kept so the context listeners can be removed without touching the throwing `renderer3D` getter. */
	private monitoredCanvas: HTMLCanvasElement | null = null;
	private frameRequestId: number | null = null;
	private lastQualityFactor = 0;

	constructor(
		@inject(DomElementsService) private domElementsService: DomElementsService,
		@inject(RENDERER_SERVICE) @optional() private rendererService?: IRendererService,
	) {
		this.logger.log("constructed");
	}

	public initialize(): void {
		this.statsPanel = new StatsPanel();
		this.domElementsService.containerElement.appendChild(this.statsPanel.element);

		// `renderer3D` throws until the map has added the custom layer, so the GPU read waits for the
		// first rendered frame rather than running here.
		this.rendererService?.addRenderLoopListener(this.handleFirstFrame);

		this.lastQualityFactor = this.qualityFactor;
		this.frameRequestId = requestAnimationFrame(this.tick);
		this.logger.log("initialized");
	}

	/** */
	private handleFirstFrame = (): void => {
		this.rendererService?.removeRenderLoopListener(this.handleFirstFrame);

		const canvas = this.rendererService?.renderer3D.domElement ?? null;

		if (canvas === null) {
			return;
		}

		this.monitoredCanvas = canvas;
		canvas.addEventListener("webglcontextlost", this.handleContextLost);
		canvas.addEventListener("webglcontextrestored", this.reportGpu);
		this.reportGpu();
	};

	/** */
	private reportGpu = (): void => {
		const renderer = this.rendererService?.renderer3D;

		if (renderer === undefined || this.statsPanel === null) {
			return;
		}

		const context = renderer.getContext();
		const debugInfo = context.getExtension("WEBGL_debug_renderer_info");
		const name = debugInfo === null ? context.getParameter(context.RENDERER) : context.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);

		this.statsPanel.setInfo(String(name));
		this.logger.log(`GPU: ${name}`);
	};

	public showStatsPanel(): void {
		this.statsPanel?.show();
	}

	public hideStatsPanel(): void {
		this.statsPanel?.hide();
	}

	public getStatsPanelElement(): HTMLElement | null {
		return this.statsPanel?.element || null;
	}

	public dispose(): void {
		if (this.frameRequestId !== null) {
			cancelAnimationFrame(this.frameRequestId);
			this.frameRequestId = null;
		}

		this.rendererService?.removeRenderLoopListener(this.handleFirstFrame);
		this.monitoredCanvas?.removeEventListener("webglcontextlost", this.handleContextLost);
		this.monitoredCanvas?.removeEventListener("webglcontextrestored", this.reportGpu);
		this.monitoredCanvas = null;

		this.statsPanel?.dispose();
		this.statsPanel = null;
	}

	private handleContextLost = (): void => {
		this.statsPanel?.setInfo("GPU CONTEXT LOST");
		this.logger.error("WebGL context lost — the 3D scene is blank until the browser restores it.");
	};

	private tick = () => {
		if (this.isAdaptivePerformanceActive) {
			this.sampleAndEvaluate();
		}

		this.statsPanel?.update();
		this.frameRequestId = requestAnimationFrame(this.tick);
	};

	private sampleAndEvaluate(): void {
		if (this.fpsSamples.length >= SAMPLES_PER_EVALUATION) {
			return;
		}

		this.frameTimes.push(performance.now());
		const msPassed = this.frameTimes[this.frameTimes.length - 1] - this.frameTimes[0];

		if (msPassed >= SAMPLE_INTERVAL_MS) {
			this.currentFps = Math.round((this.frameTimes.length / msPassed) * 1000);
			this.displayRefreshRate = Math.max(this.displayRefreshRate, this.currentFps);
			this.fpsSamples[this.sampleIndex++ % SAMPLES_PER_EVALUATION] = this.currentFps;

			if (this.fpsSamples.length === SAMPLES_PER_EVALUATION) {
				const [lower, upper] = getAcceptableFpsRange(this.displayRefreshRate);
				const aboveUpper = this.fpsSamples.filter((v) => v >= upper);
				const belowLower = this.fpsSamples.filter((v) => v < lower);

				if (aboveUpper.length > SAMPLES_PER_EVALUATION * DECISION_THRESHOLD) {
					this.qualityFactor = Math.min(1, this.qualityFactor + QUALITY_STEP);
				}

				if (belowLower.length > SAMPLES_PER_EVALUATION * DECISION_THRESHOLD) {
					this.qualityFactor = Math.max(0, this.qualityFactor - QUALITY_STEP);
				}

				if (this.lastQualityFactor !== this.qualityFactor) {
					this.flipFlopCount++;
					this.logger.log(
						`${this.qualityFactor > this.lastQualityFactor ? "incline" : "decline"}: qualityFactor=${this.qualityFactor.toFixed(2)}, fps=${this.currentFps}`,
					);
					this.lastQualityFactor = this.qualityFactor;
					this.applyQualityFactor();
				}

				if (this.flipFlopCount > MAX_FLIP_FLOPS && this.isAdaptivePerformanceActive) {
					this.isAdaptivePerformanceActive = false;
					this.handleFallback();
				}

				this.fpsSamples = [];
			}

			this.frameTimes = [];
		}
	}

	/**
	 * @name applyQualityFactor
	 * @description Quality factor changed - apply DPR and shadow settings to the renderer */
	private applyQualityFactor(): void {
		if (this.rendererService === undefined) {
			throw new Error("RendererService is not available");
		}

		const dpr = MIN_DPR + (MAX_DPR - MIN_DPR) * this.qualityFactor;
		this.setRendererPixelRatio(dpr);

		this.rendererService.renderer3D.shadowMap.enabled = this.qualityFactor > 0.3;
	}

	/** Too many flip-flops — set minimum safe quality and stop adaptive monitoring */
	private handleFallback(): void {
		if (this.rendererService === undefined) {
			throw new Error("RendererService is not available");
		}

		this.setRendererPixelRatio(MIN_DPR);
		this.rendererService.renderer3D.shadowMap.enabled = false;

		this.logger.log("fallback: locked to minimum quality");
	}

	private setRendererPixelRatio(dpr: number): void {
		this.rendererService?.setPixelRatio(dpr);
	}
}

export { PerformanceMonitorService };
